import React, { useEffect, useRef, useState } from 'react';
import type { ArticleProps, OrderProps, PostCommentStatProps, PostProps, PostReactionProps, PostReactionStatsProps, PublicPlayerProps, TextTagProps } from '../../types';
import PostHeader from './components/PostHeader';
import { useIsInViewport } from '../../Components/ViewportObserver';
import { SocialProfileApi } from '../PlayerProfile/api';

import PostReactionBar from './components/PostReactionBar';
import { Icons } from '../../Components';
import SocialOrdersList from '../SocialOrdersList';
import ImageList from './components/ImageList';
import { SocialComponentApi } from '../api';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import { FormattedText } from '../FormattedTextInput/FormattedText';
import ArticleCard from '../ArticleCard';
import UrlPreviewCard from '../UrlPreviewCard';
import { FlatList } from 'react-native';


type PostCardProps = {
    post: PostProps,
    player?:PublicPlayerProps,
    articles:ArticleProps[],
    orders:OrderProps[],
    player_id?:string,
    width:number,
    refresh_key?:string,
    onSelectImage:(image:{ url:string, width?:number, height?:number}) => void,
    onSelectTag:(tag:TextTagProps) => void,
    onSelectArticle:(article:ArticleProps) => void,
    onSelectPlayer:(p:PublicPlayerProps) => void,
    onRequestAuthenticate:() => void,
    onCopyOrder?:(order:OrderProps) => void,
    onFadeOrder?:(order:OrderProps) => void,
    onViewComments?:(post_id:string) => void
}

const sections = ['header', 'title', 'body', 'orders','images','articles', 'urls', 'footer']

const PostCard = ({ player_id, width, articles, post, player, orders, refresh_key, onRequestAuthenticate, onSelectArticle, onCopyOrder, onFadeOrder, onViewComments, onSelectImage, onSelectPlayer, onSelectTag }:PostCardProps) => {
    const Colors = useColors();
    const [ post_data, setPostData ] = useState<{
        loading:boolean,
        reactions_loaded:boolean,
        post_comment_stat:PostCommentStatProps,
        post_reaction_stats:PostReactionStatsProps[],
    }>({
        loading:false,
        reactions_loaded: false,
        post_comment_stat: { activity_post_id:post.memo_post_id, comment_count: 0 },
        post_reaction_stats: []
    })

    const { reactions_loaded, post_reaction_stats, post_comment_stat } = post_data;
    
    const [ my_data, setMyData ] = useState<{
        my_data_loading:boolean,
        my_reactions:PostReactionProps[]
    }>({
        my_data_loading:false,
        my_reactions:[]
    })
    const { my_reactions, my_data_loading } = my_data;
    const post_ref = useRef();
    const is_in_viewport = useIsInViewport(post_ref);

    useEffect(() => {
        if(!is_in_viewport){ return }
        if(reactions_loaded){ return }
        SocialProfileApi.setEnvironment();
        getPostReactionData();
        if(player_id){ getMyData(player_id) }
    },[is_in_viewport]);

    useEffect(() => {
        if(!refresh_key){ return }
        if(!reactions_loaded){ return }
        if(!is_in_viewport){ return }
        getPostReactionData(true);
        if(player_id){ getMyData(player_id, true) }
    },[refresh_key]);

    const getMyData = async(player_id:string, quiet?:boolean) => {
        if(!quiet){ setMyData({ ...my_data, my_data_loading: true }) }
        const mr = await SocialProfileApi.getReactionsByPostAndPlayer(player_id, post.memo_post_id, 'memo');
        setMyData({
            ...my_data,
            my_data_loading: false,
            my_reactions: mr
        })
    }

    const getPostReactionData = async(quiet?:boolean) => {
        if(!quiet){ setPostData({ ...post_data, loading: true }) }
        const pr_stats = await SocialProfileApi.getReactionStatsByPostId(post.memo_post_id, 'memo');
        const pc_stat = await SocialProfileApi.getCommentStatsByPostId(post.memo_post_id, 'memo');
        setPostData({
            ...post_data,
            reactions_loaded:true,
            loading: false,
            post_reaction_stats: pr_stats,
            post_comment_stat: pc_stat ? pc_stat: post_comment_stat
        })
    }

    const handleReaction = async(reaction:PostReactionProps) => {
        if(!player_id){ return onRequestAuthenticate() }
        setMyData({ ...my_data, my_data_loading: true });
        if(reaction.status == 'visible'){
            let new_reaction = await SocialComponentApi.createPostReaction(reaction);
            if(!new_reaction){ return setMyData({ ...my_data, my_data_loading: false }) }
            setMyData({ 
                ...my_data, 
                my_data_loading:false,
                my_reactions: my_reactions.concat(new_reaction)
            }) 
            let reaction_stats = post_reaction_stats.find(s => s.reaction == reaction.reaction)
            if(!reaction_stats){ reaction_stats = { activity_post_id: post.memo_post_id, reaction:reaction.reaction, reaction_count:1 } }
            else { reaction_stats = { ...reaction_stats, reaction_count: reaction_stats.reaction_count + 1 } }
            //IUpdate reaction stats
            setPostData({
                ...post_data,
                post_reaction_stats: post_reaction_stats.filter(ps => ps.reaction != reaction.reaction).concat(reaction_stats)
            })
        } else {
            let updated_reaction = await SocialComponentApi.updatePostReaction(reaction);
            if(!updated_reaction){ return setMyData({ ...my_data, my_data_loading: false }) }
            
            setMyData({
                ...my_data,
                my_data_loading: false,
                my_reactions: my_reactions.filter(r => r.post_reaction_id != updated_reaction.post_reaction_id)
            })

            let reaction_stats = post_reaction_stats.find(s => s.reaction == reaction.reaction)
            if(!reaction_stats){ reaction_stats = { activity_post_id: post.memo_post_id, reaction:reaction.reaction, reaction_count:0 } }
            else { reaction_stats = { ...reaction_stats, reaction_count: reaction_stats.reaction_count - 1 } }
            setPostData({
                ...post_data,
                post_reaction_stats: post_reaction_stats.filter(ps => ps.reaction != reaction.reaction).concat(reaction_stats)
            })
        }
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!player){ return <></> }
                return (
                    <PostHeader 
                        player={player}
                        post={post}
                        onSelectPlayer={onSelectPlayer}
                    />
                )
            case 'title':
                return (
                    <View transparent style={{ padding:10 }}>
                        <Text theme='h2'>{post.title}</Text>
                    </View>
                )
            case 'body':
                return (
                    <View transparent style={{ padding:20, paddingTop:0, paddingBottom:0 }}>
                        {post.formatted_text ?
                        <FormattedText
                            text={post.formatted_text}
                            tags={post.tags ?? []}
                            onSelectTag={(tag) => onSelectTag(tag)}
                        />
                        :
                        <Text theme='description'>{post.body}</Text>
                        }
                    </View>
                )
            case 'orders':
                if(!player || !post.memo_orders){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <SocialOrdersList
                            players={[player]}
                            orders={orders}
                            maxWidth={width - 40}
                            direction='horizontal'
                            onCopyOrder={onCopyOrder}
                            onFadeOrder={onFadeOrder}
                        />
                    </View>
                )
            case 'articles':
                if(!post.memo_articles || !articles[0]){ return <></> }
                return (
                    <View transparent>
                        <ArticleCard
                            article={articles[0]}
                            width={width}
                            onSelectArticle={onSelectArticle}
                        />
                    </View>
                )
            case 'urls':
                if(!post.memo_urls || !post.memo_urls[0]){ return <></> }
                return (
                <View transparent style={{ margin:5 }}>
                    <UrlPreviewCard
                        url={post.memo_urls[0]}
                        width={width - 10}
                        max_height={175}

                    />
                </View>
                )
            case 'images':
                if(!post.memo_images || post.memo_images.length == 0){ return <></> }
                return (
                    <View transparent style={{ padding:10 }}>
                        <ImageList
                            post_id={post.memo_post_id}
                            images={post.memo_images}
                            onSelectImage={(image) => onSelectImage(image)}
                            width={width - 20} //Take out the padding
                        />
                    </View>
                )
            case 'footer':
                return (
                    <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:5, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                        <View transparent style={{ flex:1, padding:5 }}>
                            <PostReactionBar
                                loading={my_data_loading}
                                post_id={post.memo_post_id}
                                post_reaction_stats={post_reaction_stats ?? []}
                                my_post_reactions={my_reactions ?? []}
                                onReactionSelect={(r) => handleReaction(r)}
                            />
                        </View>
                        {onViewComments ?
                        <Button
                          transparent
                            style={{ flexDirection:'row', alignItems:'center', justifyContent:'flex-end' }}
                            onPress={() => onViewComments(post.memo_post_id)}
                        >
                            <Text style={{ marginRight:4 }} theme='h2'>{post_comment_stat.comment_count}</Text>
                            <Icons.ChatIcon size={18} color={Colors.text.h1}/>
                        </Button>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }


    return (
        <View float ref={post_ref as any}>
            <FlatList
                data={sections}
                renderItem={renderSections}
                key={`post_sections`}
                keyExtractor={item => item}
            />
        </View>
    )
}

export default PostCard
